import {
  AfterViewInit,
  Component,
  DestroyRef,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { PushNotificationsService } from './shared/push-notifications/push-notification.service';
import { HeaderComponent } from '@components/header/header.component';
import { FooterComponent } from '@components/footer/footer.component';
import { GlobalState } from './store/store.model';
import { selectRouteData, selectRouteParams } from './store/router.selectors';
import { BackButtonHandlerService } from './shared/services/back-handler.service';
import { checkAppVersion } from './modules/flow/store/flow.actions';

type AppRouteData = {
  background?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  fullBleed?: boolean;
  tab?: unknown;
};

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, HeaderComponent, FooterComponent],
  templateUrl: './app.component.html',
  standalone: true,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store<GlobalState>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly pushNotifications = inject(PushNotificationsService);
  private readonly backButtonHandler = inject(BackButtonHandlerService);

  public hasTransparentBg = false;
  public hasBackground = false;
  public isFullBleed = false;
  public showHeader = true;
  public showFooter = true;

  private readonly transparentBgRoutes = [
    '/scan',
    '/get-started',
    '/log-in',
    '/log-in/reset-password',
    '/sign-up',
    '/sign-up/validation',
  ];

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const currentUrl = event.urlAfterRedirects || event.url;
        this.hasTransparentBg =
          currentUrl.includes('/scan') || this.transparentBgRoutes.includes(currentUrl);

        const root = document.documentElement;
        root.style.setProperty('--safe-area-bg', this.hasTransparentBg ? 'transparent' : '#fff');
      });
  }

  ngOnInit(): void {
    void this.initializeApp();
  }

  ngAfterViewInit(): void {
    this.toggleUiElements();
    void SplashScreen.hide();
  }

  ngOnDestroy(): void {
    this.backButtonHandler.stopListening();
  }

  private async initializeApp(): Promise<void> {
    this.store.dispatch(checkAppVersion());
    this.initializePushNotifications();
    this.watchRouteChanges();
    this.initializeDeepLinks();
    this.backButtonHandler.startListening();
  }

  private initializePushNotifications(): void {
    this.pushNotifications.initialize();
  }

  private watchRouteChanges(): void {
    this.store
      .select(selectRouteData)
      .pipe(
        distinctUntilChanged(),
        map((data: AppRouteData | null | undefined) => {
          this.hasBackground = data?.background ?? false;
          this.isFullBleed = data?.fullBleed ?? false;
          this.showHeader = !(data?.hideHeader ?? false);
          this.showFooter = !(data?.hideFooter ?? false);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private initializeDeepLinks(): void {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      const slug = event.url.split('.app').pop();
      if (!slug) return;

      void this.zone.run(() => this.router.navigateByUrl(slug));
    }).then(() => {});
  }

  private toggleUiElements(): void {
    combineLatest([
      this.store.select(selectRouteData),
      this.store.select(selectRouteParams),
    ])
      .pipe(
        filter(([data]: [AppRouteData | null | undefined, unknown]) => !!data),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(([data]) => this.applyCssVariables(data ?? {}));
  }

  private applyCssVariables(data: AppRouteData): void {
    const root = document.documentElement;

    const headerHeight = data.hideHeader ? '0px' : 'calc(74px + env(safe-area-inset-top))';
    const headerOpacity = data.hideHeader ? '0' : '1';

    const footerHeight = data.hideFooter ? '0px' : '86px';
    const footerOpacity = data.hideFooter ? '0' : '1';
    const footerTransform = data.hideFooter ? 'translateY(100%)' : 'translateY(0)';

    root.style.setProperty('--header-height', headerHeight);
    root.style.setProperty('--header-opacity', headerOpacity);
    root.style.setProperty('--footer-height', footerHeight);
    root.style.setProperty('--footer-transform', footerTransform);
    root.style.setProperty('--footer-opacity', footerOpacity);
  }
}
